import { Injectable, inject } from '@angular/core';
import { AppConfig } from 'client/app/app.config';
import { ConcernSeverity, ConcernSeverityThreshold, CONCERN_SEVERITY, CONCERN_SEVERITY_THRESHOLD } from '../../app/app-constant-types';
import { EventLoggerService } from '@services/event-logger-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { ConnectionService } from '@services/connection-service';
import { TimeService } from '@services/time-service';
import { ApiClientConstant, Table } from '@cureskin/api-client';
import { EntityEnum } from '@cureskin/api-client-rn';
import { LocalStorageService } from '@services/local-storage-service';
import { InstantCheckup, RegimenClass } from '@cureskin/api-client/src/server-api';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { firstValueFrom } from 'rxjs';
import { SideFacePhotoBottomSheetComponent } from '@shared/bottom-sheet-layouts/side-face-photo-bottom-sheet/side-face-photo-bottom-sheet.component';
import { ExperimentService } from '@services/experiment';
import { Router } from '@angular/router';
const SIDE_FACE_QUESTION_ID = 'TakeInstantCheckSideFace';

@Injectable({
  providedIn: 'root',
})

export class InstantCheckupService {
  private readonly consultationPhotoReAskDays: number = 5;
  concernSeverity: ConcernSeverity = CONCERN_SEVERITY;
  concernSeverityThreshold:ConcernSeverityThreshold = CONCERN_SEVERITY_THRESHOLD;
  private isRequestPhotoBottomSheetSkipped: boolean = false;
  private userInitialInstantCheckupImagePath?: string;
  private userInitialInstantCheckupImageClass?: string;
  private isFetchingInitialInstantCheckupImage: boolean = false;
  private sideFaceNudgeShownInSession: boolean = false;

  appConfig: AppConfig = inject(AppConfig);
  eventLogger: EventLoggerService = inject(EventLoggerService);
  appWebBridge: AppWebBridgeService = inject(AppWebBridgeService);
  connectionService: ConnectionService = inject(ConnectionService);
  timeService: TimeService = inject(TimeService);
  localStorageService: LocalStorageService = inject(LocalStorageService);
  bottomSheet: MatBottomSheet = inject(MatBottomSheet);
  experimentService: ExperimentService = inject(ExperimentService)
  router: Router = inject(Router);
  constructor() {}

  getSeverityFromConcern(checkup: any):string | object {
    const detectedCount = checkup?.BoundingBoxes?.length || checkup?.PolygonPointsSequences?.length;
    let severity = '';

    if (!detectedCount && checkup.class !== this.appConfig.Shared.InstantCheckup.Concerns.MELASMA) return checkup;
    if (checkup.class === this.appConfig.Shared.InstantCheckup.Concerns.MELASMA) {
      severity = typeof checkup.Severity === 'string' ? checkup.Severity[0].toUpperCase() + checkup.Severity.slice(1).toLowerCase() : '';
    }
    if (detectedCount && this.concernSeverityThreshold[checkup.class] && (
      checkup.class !== this.appConfig.Shared.InstantCheckup.Concerns.MELASMA)) {
      const thresholdRange = this.concernSeverityThreshold[checkup.class];
      if (detectedCount > thresholdRange.moderate) severity = this.concernSeverity.Severe;
      else if (detectedCount > thresholdRange.mild) severity = this.concernSeverity.Moderate;
      else severity = this.concernSeverity.Mild;
    }
    return severity;
  }

  async loadImage(src: string): Promise<HTMLImageElement> {
    if (!src) return new Image();
    return new Promise((resolve: (image: HTMLImageElement) => void, reject: (error: Error) => void): void => {
      const image = new Image();
      image.src = src;
      image.onload = (): void => resolve(image);
      image.onerror = (): void => reject(new Error('Cant process image'));
    });
  }

  startCapture(extraData: Record<string, any> = {}, tag?: string, fromTakePhotoBanner?: any,): boolean {
    this.eventLogger.cleverTapEvent('instantCheckupInitiated', JSON.stringify({ type: tag }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    this.appWebBridge.logEventInBranchAndFirebaseFromiOS({
      branch: { name: 'instantCheckupInitiated' },
      firebase: { name: 'instantCheckupInitiated' },
    });
    this.eventLogger.trackEvent('take_photo_clicked', { type: tag });
    this.eventLogger.trackPeopleIncrement({ people_take_photo_clicked: 1 })

    if (this.appWebBridge.isAppWebBridgeLoaded()) {
      this.appWebBridge.notifyCaptureImage(tag, {}, extraData);
      return true;
    }

    return false;
  }

  /** Gallery upload is offered only for hair photo types, on app builds exposing the bridge method, behind the experiment. */
  async isGalleryUploadEnabled(type: string): Promise<boolean> {
    const galleryUploadTypes: Array<string> = [
      this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT,
      this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP,
    ];
    if (!galleryUploadTypes.includes(type)) return false;
    await this.experimentService.experimentsLoaded;
    return this.experimentService.hairPhotoGalleryUploadEnabled && this.appWebBridge.isGalleryPickerSupported();
  }

  startGalleryCapture(extraData: Record<string, unknown> = {}, tag?: string): boolean {
    this.eventLogger.trackEvent('upload_from_gallery_clicked', { type: tag });
    if (!this.appWebBridge.isGalleryPickerSupported()) return false;
    this.appWebBridge.requestGalleryImage(tag, {}, extraData);
    return true;
  }

  getInstantCheckupOfType(user: Table.User, days: number, type:string[]): Promise<Table.InstantCheckup[]> {
    return this.connectionService.fetchInstantCheckup({
      userId: user.id,
      limit: 1,
      project: ['type'],
      type,
      dateRange: { startDate: this.timeService.removeDays(new Date(), days), endDate: new Date() },
    });
  }

  async getRecentInstantCheckupOfType(user: Table.User, type: string): Promise<InstantCheckup | undefined> {
    const [checkup] = await this.connectionService.fetchInstantCheckup({
      userId: user.id,
      type: [type],
      limit: 1,
      project: ['type', 'imagePath'],
      dateRange: { startDate: this.timeService.removeDays(new Date(), this.consultationPhotoReAskDays), endDate: new Date() },
    });
    return checkup;
  }

  /**
   * Required instant-checkup types for the "Complete Your Profile" banner, keyed by regimen class.
   * FACE -> front + both side faces; HAIR -> hair front + top.
   */
  private readonly completeProfileCheckupTypes: Record<string, Array<string>> = {
    [ApiClientConstant.Regimen.Class.FACE]: [
      ApiClientConstant.InstantCheckup.Type.FRONT_FACE,
      ApiClientConstant.InstantCheckup.Type.LEFT_SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.RIGHT_SIDE_FACE,
    ],
    [ApiClientConstant.Regimen.Class.HAIR]: [
      ApiClientConstant.InstantCheckup.Type.HAIR_FRONT,
      ApiClientConstant.InstantCheckup.Type.HAIR_TOP,
    ],
  };

  getCompleteProfileCheckupTypes(regimenClass: RegimenClass): Array<string> {
    return this.completeProfileCheckupTypes[regimenClass] ?? [];
  }

  async getCheckupsForClass(userId: string, regimenClass: RegimenClass): Promise<Record<string, InstantCheckup | null>> {
    const types = this.getCompleteProfileCheckupTypes(regimenClass);
    if (!userId || !types.length) return {};
    const response = await this.connectionService.getInstantCheckupsByTypes(types, 1, false);
    const grouped = response?.instantCheckups ?? {};
    return Object.fromEntries(
      types.map((type: string): [string, InstantCheckup | null] => [type, grouped[type]?.[0] ?? null]),
    );
  }

  async shouldShowRequestPhotoBottomSheet(user: Table.User, photoType: string): Promise<boolean> {
    if (this.getIsRequestPhotoBottomSheetSkipped()) return false;
    const requiredPhotoTypes = this.getRequiredPhotoTypesForReacquisition(photoType);
    if (requiredPhotoTypes.includes(photoType) || this.isInvalidPhotoType(requiredPhotoTypes[0])) return false;
    const instantCheckup = await this.getInstantCheckupOfType(user, 7, requiredPhotoTypes);
    if (instantCheckup.length > 0) {
      return false;
    };
    return true;
  }

  getRequiredPhotoTypesForReacquisition(currentPhotoType: string): Array<string> {
    switch (currentPhotoType) {
      case this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE:
        return [this.appConfig.Shared.InstantCheckup.Type.LEFT_SIDE_FACE, this.appConfig.Shared.InstantCheckup.Type.RIGHT_SIDE_FACE];
      case this.appConfig.Shared.InstantCheckup.Type.LEFT_SIDE_FACE:
        return [this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE];
      case this.appConfig.Shared.InstantCheckup.Type.RIGHT_SIDE_FACE:
        return [this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE];
      default:
        return [this.appConfig.Shared.InstantCheckup.Type.INVALID];
    }
  }

  isInvalidPhotoType(photoType: string): boolean {
    return photoType === this.appConfig.Shared.InstantCheckup.Type.INVALID;
  }

  async shouldAnalyzeHair(photoType: string, gender: string): Promise<boolean> {
    await this.experimentService.experimentsLoaded;
    const allowedPhotoTypes = this.experimentService.getHairAiPhotoTypesForGender(gender);
    if (allowedPhotoTypes) return allowedPhotoTypes.includes(photoType);
    const isFemaleHairFront = photoType === this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT
      && gender === this.appConfig.Shared.Gender.FEMALE;
    return !isFemaleHairFront;
  }

  setIsRequestPhotoBottomSheetSkipped(value: boolean): void {
    this.isRequestPhotoBottomSheetSkipped = value;
  }

  getIsRequestPhotoBottomSheetSkipped(): boolean {
    return this.isRequestPhotoBottomSheetSkipped
  }

  isSideFaceNudgeShownInSession(): boolean {
    return this.sideFaceNudgeShownInSession;
  }

  getValidInstantCheckupTypes(): string[] {
    return Object.keys(this.appConfig.Shared.InstantCheckup.Type).filter((type_: string): boolean => {
      return ![this.appConfig.Shared.InstantCheckup.Type.INVALID,
        this.appConfig.Shared.InstantCheckup.Type.REPORT,
        this.appConfig.Shared.InstantCheckup.Type.BODY,
        this.appConfig.Shared.InstantCheckup.Type.OTHER].includes(type_);
    });
  }

  async getUserInitialInstantCheckupImage(userId: string, activeRegimen: any, regimenClass?: string): Promise<string | undefined> {
    const resolvedClass = regimenClass || 'FACE';
    if (this.userInitialInstantCheckupImagePath && this.userInitialInstantCheckupImageClass === resolvedClass) {
      return this.userInitialInstantCheckupImagePath;
    }
    if (this.isFetchingInitialInstantCheckupImage) return this.userInitialInstantCheckupImagePath;

    if (!userId || !activeRegimen) return undefined;

    // Clear cache if class changed
    if (this.userInitialInstantCheckupImageClass && this.userInitialInstantCheckupImageClass !== resolvedClass) {
      this.userInitialInstantCheckupImagePath = undefined;
    }

    this.isFetchingInitialInstantCheckupImage = true;
    this.userInitialInstantCheckupImageClass = resolvedClass;
    try {
      const isHair = resolvedClass === 'HAIR';
      const photoTypes = isHair
        ? [
            this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT,
            this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP,
            this.appConfig.Shared.InstantCheckup.Type.HAIR,
          ]
        : [
            this.appConfig.Shared.InstantCheckup.Type.LEFT_SIDE_FACE,
            this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE,
            this.appConfig.Shared.InstantCheckup.Type.RIGHT_SIDE_FACE,
          ];
      const preferredType = isHair
        ? this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT
        : this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE;

      const instantCheckups = await this.connectionService.fetchInstantCheckup({
        userId,
        type: photoTypes,
        limit: 50,
        sortAscending: true,
      });

      if (!instantCheckups || instantCheckups.length === 0) {
        return undefined;
      }

      const activeFromIso = activeRegimen.activeFromDate?.iso ?? activeRegimen.activeFrom?.iso;
      let candidateCheckups: Array<any> = instantCheckups;
      if (activeFromIso) {
        const activeFrom = new Date(activeFromIso);
        const startOfDay = new Date(activeFrom);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(activeFrom);
        endOfDay.setHours(23, 59, 59, 999);

        const inRange = instantCheckups.filter((checkup: any): boolean => {
          const created = checkup.createdAt?.iso ?? checkup.createdAt;
          if (!created) return false;
          const createdAt = new Date(created);
          return createdAt >= startOfDay && createdAt <= endOfDay;
        });

        if (inRange.length) {
          candidateCheckups = inRange;
        }
      }

      const preferredCheckup = candidateCheckups.find((checkup: any): boolean =>
        checkup.type === preferredType);

      const chosenCheckup = preferredCheckup ?? candidateCheckups[0];
      this.userInitialInstantCheckupImagePath = chosenCheckup?.imagePath;
      return this.userInitialInstantCheckupImagePath;
    } catch (e) {
      return undefined;
    } finally {
      this.isFetchingInitialInstantCheckupImage = false;
    }
  }

  clearInitialInstantCheckupImageCache(): void {
    this.userInitialInstantCheckupImagePath = undefined;
    this.userInitialInstantCheckupImageClass = undefined;
    this.isFetchingInitialInstantCheckupImage = false;
  }


  async validateAndOpenSideFacePhotoBottomSheet(data: {
    currentInstantCheckup: InstantCheckup,
    isReacquisitionUser: boolean,
    isConsultationOrFollowup: boolean,
    userId: string,
    consultationSession?: Table.ConsultationSession,
    callback: (messageId?: string) => void | Promise<void>,
  }): Promise<string | undefined> {
    const { currentInstantCheckup, isReacquisitionUser, isConsultationOrFollowup, userId, consultationSession, callback } = data;

    if (!this.experimentService.sidePhotoNudgeInFrontFaceExperiment?.enable) return undefined

    if (isReacquisitionUser || !isConsultationOrFollowup) return undefined;

    const isCurrentPhotoFrontFace = currentInstantCheckup.type === ApiClientConstant.InstantCheckup.Type.FRONT_FACE;
    if (!isCurrentPhotoFrontFace) return undefined;


    if (this.sideFaceNudgeShownInSession || this.localStorageService.getBooleanValue('sideFaceUploadSkipped')) return undefined;

    const hasSideFacePhoto = await this.hasRecentSideFacePhoto(userId);

    if (!hasSideFacePhoto) {
      const availableImages = [{ image: currentInstantCheckup.imagePath, side: currentInstantCheckup.type }];
      const bottomSheetRef = this.bottomSheet.open(SideFacePhotoBottomSheetComponent, {
        data: {
          availableImages,
          requiredSide: ApiClientConstant.InstantCheckup.Type.SIDE_FACE,
          fetchSideFaceQuestionMessageId: (): Promise<string | undefined> =>
            this.fetchSideFaceQuestionMessageId(consultationSession),
          callback: (messageId?: string): void | Promise<void> => {
            return Promise.resolve(callback(messageId));
          },
        },
        disableClose: true,
      });
      bottomSheetRef.backdropClick().subscribe((): void => {
        this.sideFaceNudgeShownInSession = true;
        bottomSheetRef.dismiss(SideFacePhotoBottomSheetComponent.DISMISS_SKIP);
      });

      const result = await firstValueFrom(bottomSheetRef.afterDismissed());
      this.sideFaceNudgeShownInSession = true;
      return result as string | undefined;
    }
    return undefined;
  }

  private async hasRecentSideFacePhoto(userId: string): Promise<boolean> {
    const sideFaceTypes = [
      ApiClientConstant.InstantCheckup.Type.SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.LEFT_SIDE_FACE,
      ApiClientConstant.InstantCheckup.Type.RIGHT_SIDE_FACE,
    ];
    const fiveDaysAgo = this.timeService.removeDays(new Date(), 5);
    const recentSideFacePhotos: InstantCheckup[] = await this.connectionService.fetchInstantCheckup({
      userId,
      type: sideFaceTypes,
      dateRange: { startDate: fiveDaysAgo, endDate: new Date() },
      limit: 1,
      project: ['type'],
    });
    return recentSideFacePhotos.length > 0;
  }

  async fetchSideFaceQuestionMessageId(consultationSession?: Table.ConsultationSession): Promise<string | undefined> {
    if (!consultationSession) return undefined;
    const [latestQuestion]: Array<Table.ConsultationSessionChat> = await this.connectionService.findConsultationSessionChat({
      where: { consultationSession, Owner: EntityEnum.ChatMessage.Owner.BOT },
      limit: 1,
      project: ['questionId', 'objectId'],
      descending: 'createdAt',
    });
    return latestQuestion?.get('questionId') === SIDE_FACE_QUESTION_ID ? latestQuestion.id : undefined;
  }

  async navigateToInstantCheckupPreview(queryParams: Record<string, unknown>): Promise<void> {
    await this.router.navigate(['/user/instantCheckup/preview'], { queryParams });
  }

  async fetchInstantCheckupsByIds(ids: string[]): Promise<InstantCheckup[]> {
    try {
      return await this.connectionService.fetchInstantCheckup({
        id: ids,
        project: ['type', 'imagePath'],
        sortAscending: true,
      });
    } catch {
      return [];
    }
  }

  getUserLatestImage(checkups: InstantCheckup[] | null | undefined): string | undefined {
    if (!checkups?.length) return undefined;
    const sorted = [...checkups].sort(
      (a: InstantCheckup, b: InstantCheckup): number =>
        +new Date(b.createdAt ?? 0) - +new Date(a.createdAt ?? 0),
    );
    return sorted.find((checkup: InstantCheckup): boolean => !!checkup.imagePath)?.imagePath;
  }

  /**
   * Photo questionId (consultation trees) → the checkup type it captures. Lazy getters (not field
   * initializers) so constructing the service never touches appConfig — specs that inject it with a
   * slim appConfig mock must not throw at construction.
   */
  private get photoQuestionToType(): Record<string, string> {
    return {
      TakeInstantCheckFrontFace: this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE,
      TakeInstantCheckSideFace: this.appConfig.Shared.InstantCheckup.Type.RIGHT_FACE,
      HairfallPhotoTop: this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP,
      HairfallPhoto: this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT,
    };
  }

  /** The other photo of the consultation pair (FACE: front↔side; HAIR: front↔top). */
  private get photoPairAlternate(): Record<string, string> {
    return {
      [this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT]: this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP,
      [this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP]: this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT,
    };
  }

  /** Card caption per photo type (app-consistent wording). */
  private get photoTypeLabel(): Record<string, string> {
    return {
      [this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP]: $localize`:@@HAIR_TOP:Hair Top`,
      [this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT]: $localize`:@@HAIR_FRONT:Hair Front`,
    };
  }

  getPhotoCheckupType(questionId: string): string {
    return this.photoQuestionToType[questionId] || '';
  }

  getPhotoQuestionId(checkupType: string): string {
    const entry = Object.entries(this.photoQuestionToType).find(([, type]) => type === checkupType);
    return entry ? entry[0] : '';
  }

  getAlternatePhotoType(checkupType: string): string {
    return this.photoPairAlternate[checkupType] || '';
  }

  getPhotoTypeLabel(checkupType: string): string {
    return this.photoTypeLabel[checkupType] || '';
  }

  /** Checkup-type illustration per gender (rebranded CDN set, shared by checkup cards and the quiz photo card). */
  getPhotoIllustrationUrl(checkupType: string, gender: string): string {
    const illustrations: Record<string, { female: string; male: string }> = {
      [this.appConfig.Shared.InstantCheckup.Type.FRONT_FACE]: {
        female: 'https://cdn.cureskin.com/app/img/female-front-face-rebranded.png',
        male: 'https://cdn.cureskin.com/app/img/male-front-face-rebranded.png',
      },
      [this.appConfig.Shared.InstantCheckup.Type.RIGHT_FACE]: {
        female: 'https://cdn.cureskin.com/app/img/female-right-side-face-rebranded.png',
        male: 'https://cdn.cureskin.com/app/img/male-right-face-rebranded.png',
      },
      [this.appConfig.Shared.InstantCheckup.Type.LEFT_FACE]: {
        female: 'https://cdn.cureskin.com/app/img/female-left-side-face-rebranded.png',
        male: 'https://cdn.cureskin.com/app/img/male-left-face_rebranded.png',
      },
      [this.appConfig.Shared.InstantCheckup.Type.HAIR_FRONT]: {
        female: '/assets/images/upload_female_hair_front.svg',
        male: '/assets/images/upload_male_hair_front.svg',
      },
      [this.appConfig.Shared.InstantCheckup.Type.HAIR_TOP]: {
        female: '/assets/images/upload_female_hair_top.svg',
        male: '/assets/images/upload_male_hair_top.svg',
      },
    };
    const urls = illustrations[checkupType];
    if (!urls) return '';
    return gender === this.appConfig.Shared.Gender.MALE ? urls.male : urls.female;
  }
}
